import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import Button from '../Button/Button';
import { useTheme } from '../../../../../ThemeContext';

import styles from './NavMenu.module.scss';

type NavItem = { label: string; contentId: string; to: string };

interface NavMenuProps {
  navItems: NavItem[];
  onNavClick: (to: string, item: NavItem) => void;
}

const NavMenu: React.FC<NavMenuProps> = ({
  navItems,
  onNavClick,
}) => {
  const { universalStyles } = useTheme();
  const { t } = useTranslation('common');
  const location = useLocation();
  
  const navContainerRef = useRef<HTMLDivElement>(null);
  const [visibleNavItems, setVisibleNavItems] = useState<NavItem[]>([]);
  const [overflowNavItems, setOverflowNavItems] = useState<NavItem[]>([]);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const moreMenuRef = useRef<HTMLDivElement>(null);

  // Calculate visible/overflow nav items
  const calculateNavItems = useCallback(() => {
    if (!navContainerRef.current) {
      setTimeout(calculateNavItems, 5);
      return;
    }
    const containerWidth = navContainerRef.current.offsetWidth;
    let usedWidth = 0;
    let lastVisibleIndex = navItems.length - 1;

    const tempSpan = document.createElement('span');
    tempSpan.style.visibility = 'hidden';
    tempSpan.style.position = 'absolute';
    tempSpan.style.whiteSpace = 'nowrap';
    tempSpan.style.fontSize = '16px';
    document.body.appendChild(tempSpan);

    const moreButtonWidth = 100;

    for (let i = 0; i < navItems.length; i++) {
      tempSpan.innerText = navItems[i].label;
      const itemWidth = tempSpan.offsetWidth + 40;
      if (usedWidth + itemWidth > containerWidth - moreButtonWidth) {
        lastVisibleIndex = i - 1;
        break;
      }
      usedWidth += itemWidth;
    }
    document.body.removeChild(tempSpan);

    setVisibleNavItems(navItems.slice(0, lastVisibleIndex + 1));
    setOverflowNavItems(navItems.slice(lastVisibleIndex + 1));
  }, [navItems]);

  // recalculate on mount and resize
  useEffect(() => {
    calculateNavItems();

    const handleResize = () => {
      setMoreMenuOpen(false);
      calculateNavItems();
    };

    window.addEventListener('resize', handleResize);

    let resizeObserver: ResizeObserver | undefined;
    const observedNode = navContainerRef.current;
    if (observedNode && 'ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(observedNode);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeObserver && observedNode) {
        resizeObserver.unobserve(observedNode);
      }
    };
  }, [calculateNavItems]);

  // menu close on outside click
  useEffect(() => {
    if (!moreMenuOpen) return;
    function handleClickOutside(event: MouseEvent) {
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target as Node)) {
        setMoreMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [moreMenuOpen]);

  return (
    <div ref={navContainerRef} className={styles.navItemsContainer}>
      {visibleNavItems.map((item) => (
        <span
          key={item.to}
          onClick={() => onNavClick(item.to, item)}
          className={styles.navItem}
        >
          <Button
            activeClassname={styles.headerButton}
            label={item.label}
            to={item.to}
            variant="text"
            textStyle={universalStyles.navbarText}
          />
        </span>
      ))}
      {overflowNavItems.length > 0 && (
        <div style={{ position: 'relative' }} ref={moreMenuRef}>
          <Button
            variant="text"
            textStyle={universalStyles.navbarText}
            onClick={() => setMoreMenuOpen((open) => !open)}
            aria-expanded={moreMenuOpen}
            aria-haspopup="menu"
          >
            <span className={styles.moreButtonContainer}>
              {t('more')}
              {moreMenuOpen ? (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
                  xmlns="http://www.w3.org/2000/svg" className={styles.seeMoreIcon} style={{ marginLeft: 4 }}>
                  <path
                    d="M11.9874 8.99998C12.1207 8.99998 12.2457 9.02098 12.3624 9.06297C12.479 9.10431 12.5874 9.17497 12.6874 9.27497L17.3124 13.9C17.4957 14.0833 17.583 14.3126 17.5744 14.588C17.5664 14.8626 17.4707 15.0916 17.2874 15.275C17.104 15.4583 16.8707 15.55 16.5874 15.55C16.304 15.55 16.0707 15.4583 15.8874 15.275L11.9874 11.375L8.06236 15.3C7.87903 15.4833 7.6497 15.571 7.37436 15.563C7.0997 15.5543 6.8707 15.4583 6.68737 15.275C6.50403 15.0916 6.41236 14.8583 6.41236 14.575C6.41236 14.2916 6.50403 14.0583 6.68737 13.875L11.2874 9.27497C11.3874 9.17497 11.4957 9.10431 11.6124 9.06297C11.729 9.02098 11.854 8.99998 11.9874 8.99998Z"
                    fill={universalStyles.seeMoreIcon.color}
                  />
                </svg>
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
                  xmlns="http://www.w3.org/2000/svg" className={styles.seeMoreIcon} style={{ marginLeft: 4 }}>
                  <path
                    d="M12 15.5635C11.8667 15.5635 11.7417 15.5425 11.625 15.5005C11.5083 15.4592 11.4 15.3885 11.3 15.2885L6.675 10.6635C6.49167 10.4802 6.40433 10.2508 6.413 9.9755C6.421 9.70084 6.51667 9.47184 6.7 9.2885C6.88333 9.10517 7.11667 9.0135 7.4 9.0135C7.68333 9.0135 7.91667 9.10517 8.1 9.2885L12 13.1885L15.925 9.2635C16.1083 9.08017 16.3377 8.9925 16.613 9.0005C16.8877 9.00917 17.1167 9.10517 17.3 9.2885C17.4833 9.47184 17.575 9.70517 17.575 9.9885C17.575 10.2718 17.4833 10.5052 17.3 10.6885L12.7 15.2885C12.6 15.3885 12.4917 15.4592 12.375 15.5005C12.2583 15.5425 12.1333 15.5635 12 15.5635Z"
                    fill={universalStyles.seeMoreIcon.color}
                  />
                </svg>
              )}
            </span>
          </Button>
          <div
            style={{ display: moreMenuOpen ? 'block' : 'none' }}
            className={styles.moreMenu}
            role="menu"
          >
            {overflowNavItems.map((item, idx) => {
              const isActive = location?.pathname === item.to;
              return (
                <React.Fragment key={item.to}>
                  {idx !== 0 && ( 
                    <div className={styles.menuDivider} />
                  )}
                  <span
                    onClick={() => {
                      onNavClick(item.to, item);
                      setMoreMenuOpen(false);
                    }}
                    className={`${styles.moreMenuItem} ${isActive ? styles.activeMenuItem : ''}`}
                    role="menuitem"
                  >
                    <Button
                      label={item.label}
                      to={item.to}
                      variant="text"
                      textStyle={universalStyles.navbarText}
                    />
                  </span>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default NavMenu;
